import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Card, CardContent, CardHeader, makeStyles, Typography, Icon, Button } from '@material-ui/core'

import PaymentMethodDialog from '../../Dialogs/Payments/PaymentMethodDialog'
import FailedChargesWarningDialog from '../../Dialogs/Payments/FailedChargesWarningDialog'
import { BAD_STRIPE_CHARGE_STATUSES } from '../../../utils'
import { processFailedChargesForHousehold } from '../../../actions/households/households.actions'
import { useSelector } from 'react-redux'

import clsx from 'clsx'

const useStyles = ({ makeResponsive }) =>
	makeStyles((theme) => {
		const styles = {
			card: {
				borderRadius: 6,
				backgroundColor: '#F3F3F3'
			},
			paymentWrapper: {
				display: 'flex',
				alignItems: 'center',
				flexWrap: 'wrap',
				marginBottom: 8,
				'& > *': {
					marginRight: makeResponsive ? 0 : 16
				}
			},
			paymentIcon: {
				width: 24,
				height: 24,
				padding: 0,
				'&:before': {
					fontSize: '20px',
					lineHeight: '24px',
					color: '#777679'
				}
			}
		}

		if (makeResponsive) {
			styles.card['width'] = '100%'
			styles.paymentWrapper['gap'] = 12
		}

		return styles
	})

const PaymentMethodCard = (props) => {
	let { household, makeResponsive = false, facility: facilityProp, defaultFacility, paymentMethods, onCardSaved } = props
	const classes = useStyles({ makeResponsive })()
	const dispatch = useDispatch()
	const facilities = useSelector((state) => state.specific.facilities.facilities)

	const [isShown, setIsShown] = useState(false)
	const [dialogFacilityId, setDialogFacilityId] = useState(null)
	const [failedChargesWarningShown, setFailedChargesWarningShown] = useState(false)
	const [failedChargesTotalInCents, setFailedChargesTotalInCents] = useState(0)

	const lockedToFacility = Boolean(facilityProp)

	const homeFacility = useMemo(() => {
		if (defaultFacility?._id) return defaultFacility

		const facilityId = household?.default_facility_id
		if (!facilityId || !facilities?.length) return null

		return facilities.find((facility) => String(facility._id) === String(facilityId)) || null
	}, [defaultFacility, household?.default_facility_id, facilities])

	const targetFacility = useMemo(() => {
		if (facilityProp?.publishable_stripe_key || facilityProp?.name) return facilityProp

		const facilityId = facilityProp?._id || homeFacility?._id
		if (!facilityId || !facilities?.length) return homeFacility

		return facilities.find((facility) => String(facility._id) === String(facilityId)) || homeFacility
	}, [facilityProp, homeFacility, facilities])

	const clientFacilityId = household?.default_facility_id || homeFacility?._id || null
	const canAddCard = Boolean(clientFacilityId)

	const cardRows = useMemo(() => {
		if (lockedToFacility) {
			const method = paymentMethods?.[0]

			return [
				{
					facility_id: targetFacility?._id || facilityProp?._id,
					facility_name: targetFacility?.name || '',
					card: method
						? {
								last4: method.last4,
								exp_month: method.exp_month,
								exp_year: method.exp_year,
								brand: method.brand
							}
						: null
				}
			]
		}

		if (household?.facility_cards?.length) {
			const seen = new Set()

			return household.facility_cards.flatMap((entry) => {
					const facilityId = entry.facility_id ? String(entry.facility_id) : ''
					if (facilityId && seen.has(facilityId)) return []
					if (facilityId) seen.add(facilityId)

					const methods = entry.payment_methods || []
					const seenCards = new Set()
					const uniqueMethods = methods.filter((method) => {
						const identity = method?.last4
							? `${method.brand || ''}:${method.last4}:${method.exp_month}:${method.exp_year}`
							: method?.id
						if (!identity || seenCards.has(identity)) return false
						seenCards.add(identity)

						return true
					})

					return uniqueMethods.length
						? uniqueMethods.map((method) => ({
							facility_id: entry.facility_id,
							facility_name: entry.facility_name,
							card: method
						}))
						: [{
							facility_id: entry.facility_id,
							facility_name: entry.facility_name,
							card: null
						}]
					})
		}

		const stripeCard = household?.stripe?.data?.[0]
		if (!stripeCard) return []

		const card = stripeCard.type === 'card' ? stripeCard.card : stripeCard
		const home = homeFacility || (facilities || []).find((facility) => String(facility._id) === String(household.default_facility_id))

		return [
			{
				facility_id: household.default_facility_id,
				facility_name: home?.name || '',
				card
			}
		]
	}, [lockedToFacility, paymentMethods, targetFacility, facilityProp, household, facilities, homeFacility])

	useEffect(() => {
		if (household && household.transactions) {
			let failedChargesTotal = 0
			household.transactions.forEach(({ stripe_charge_status, amount, stripe_error_code }) => {
				if (BAD_STRIPE_CHARGE_STATUSES.includes(stripe_charge_status) && stripe_error_code !== '400') {
					failedChargesTotal += amount
				}
			})
			setFailedChargesTotalInCents(failedChargesTotal)
		}
	}, [household])

	const openDialog = useCallback(() => {
		if (!clientFacilityId) return

		setDialogFacilityId(clientFacilityId)

		if (failedChargesTotalInCents > 0) {
			setFailedChargesWarningShown(true)
			setIsShown(true)
		} else {
			setIsShown(true)
		}
	}, [failedChargesTotalInCents, clientFacilityId])

	const handleFailedChargesConfirmation = useCallback((action) => {
		if (action === 'confirm') {
			setFailedChargesWarningShown(false)
		} else {
			setFailedChargesWarningShown(false)
			setIsShown(false)
		}
	}, [])

	const handleOnPaymentMethodUpdated = useCallback(
		(updated) => {
			if (typeof onCardSaved === 'function') {
				onCardSaved(updated)
			}
			if (updated && household && failedChargesTotalInCents > 0) {
				// process payments
				dispatch(
					processFailedChargesForHousehold({
						householdId: household._id,
						failedChargesTotalInCents
					})
				)
			}
		},
		[failedChargesTotalInCents, household, onCardSaved]
	)

	return (
		<Card className={classes.card}>
			<CardHeader title="Payment Method" />
			<CardContent>
				<Typography variant="overline">Current card on file</Typography>
				{cardRows.some((row) => row.card) ? (
					cardRows
						.filter((row) => row.card)
						.map((row) => (
							<div className={classes.paymentWrapper} key={`${row.facility_id || row.facility_name}-${row.card?.last4 || ''}-${row.card?.exp_year || ''}`}>
								<Icon className={clsx(['fas fa-credit-card', classes.paymentIcon])} />
								<Typography
									style={{
										fontWeight: 600,
										fontSize: '16px',
										lineHeight: '22px'
									}}>
									**** {row.card.last4}
								</Typography>
								<Typography
									style={{
										fontWeight: 600,
										fontSize: '16px',
										lineHeight: '22px',
										color: '#777679'
									}}>
									{row.card?.exp_month
										? `${row.card.exp_month}/${row.card.exp_year}`
										: row.card.type?.toUpperCase()}
								</Typography>
								{row.facility_name ? (
									<Typography
										style={{
											fontWeight: 600,
											fontSize: '16px',
											lineHeight: '22px',
											color: '#777679'
										}}>
										{row.facility_name}
									</Typography>
								) : null}
								{canAddCard ? (
									<Button
										style={{
											fontWeight: 600,
											fontSize: '16px',
											lineHeight: '22px',
											color: '#6B4AD6',
											textTransform: 'none',
											padding: '6px 5px'
										}}
										onClick={openDialog}>
										Change
									</Button>
								) : null}
							</div>
						))
				) : (
					<div className={classes.paymentWrapper}>
						<Icon className={clsx(['fas fa-credit-card', classes.paymentIcon])} />
						{canAddCard ? (
							<Button
								style={{
									fontWeight: 600,
									fontSize: '16px',
									lineHeight: '22px',
									color: '#6B4AD6',
									textTransform: 'none',
									padding: '6px 5px'
								}}
								onClick={openDialog}>
								Add Payment Method
							</Button>
						) : null}
					</div>
				)}
				{!lockedToFacility && cardRows.some((row) => row.card) && canAddCard ? (
					<Button
						style={{
							fontWeight: 600,
							fontSize: '16px',
							lineHeight: '22px',
							color: '#6B4AD6',
							textTransform: 'none',
							padding: '6px 5px'
						}}
						onClick={openDialog}>
						Add card
					</Button>
				) : null}
				<PaymentMethodDialog
					isShown={isShown}
					setIsShown={setIsShown}
					facilityId={dialogFacilityId || clientFacilityId}
					lockFacility
					onPaymentMethodUpdated={handleOnPaymentMethodUpdated}
				/>
				<FailedChargesWarningDialog
					style={{ zIndex: 3000 }}
					amountInCents={failedChargesTotalInCents}
					isShown={failedChargesWarningShown}
					setFailedChargesActionTaken={handleFailedChargesConfirmation}
				/>
			</CardContent>
		</Card>
	)
}

export default PaymentMethodCard
